import type {
  ArenaTableRowData,
  EntryResultStatus
} from '@library/types/arenaContest';
import { Cell, Row } from '@library/components/table';
import { ARENA_TABLE_HEADER } from '@bo-constants/arenaContest';
import { Badge } from '@library/components/badges/Badge';
import { cn } from '@library/component-utils/cn';
import { useMemo } from 'react';

export interface MemberEntryModalArenaTableRowProps {
  entryId: string | undefined;
  columns: Array<string>;
  rowData: ArenaTableRowData;
}

export const MemberEntryModalArenaTableRow = ({
  columns,
  entryId,
  rowData
}: MemberEntryModalArenaTableRowProps) => {
  const columnAttributes = useMemo(
    () => (column: string) => {
      const columnConfig = ARENA_TABLE_HEADER.find(
        (col) => col.column === column
      );
      const widthClass = columnConfig?.width;
      const value = rowData[column] as EntryResultStatus;
      const isBadge = ['result', 'entryResult'].includes(column);
      const isScoreColumn = ['score'].includes(column);

      return {
        column,
        isBadge,
        isScoreColumn,
        value,
        widthClass
      };
    },
    [rowData]
  );

  const isSelectedEntry = entryId === rowData.entryId;

  return (
    <Row
      dataTestId={`arena-table-entry-row-${entryId}`}
      rowClassName={cn(
        'last:border-b',
        isSelectedEntry ? 'bg-soFresh-130 hover:bg-soFresh-140' : ''
      )}
    >
      {columns.map((column) => {
        const { isBadge, isScoreColumn, value, widthClass } =
          columnAttributes(column);

        return (
          <Cell
            key={column}
            cellClassName={cn('flex justify-center', widthClass ?? '')}
            data-testid={`arena-table-entry-cell-${column}`}
          >
            <div
              className={cn(
                isScoreColumn
                  ? 'border-soClean-180 bg-soFresh-130 text-soClean-180 flex h-7 items-center rounded-md border p-2.5 font-semibold leading-none'
                  : ''
              )}
            >
              {isBadge ? <Badge type={value} /> : value}
            </div>
          </Cell>
        );
      })}
    </Row>
  );
};
